import React, { useState, useEffect } from 'react';
import api from '../../../services/api';

const ServiceGroupCBCSettings = ({ groupId, groupName }) => {
  const [settings, setSettings] = useState(null);
  const [effectiveSettings, setEffectiveSettings] = useState(null);
  const [platformPresets, setPlatformPresets] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (groupId) {
      fetchSettings();
    }
  }, [groupId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const [settingsRes, effectiveRes] = await Promise.all([
        api.get(`/service-groups/${groupId}/cbc`),
        api.get(`/service-groups/${groupId}/cbc/effective`)
      ]);

      setSettings(settingsRes.data.cbcOverrides || { overrideEnabled: false });
      setPlatformPresets(settingsRes.data.platformPresets || {});
      setEffectiveSettings(effectiveRes.data.effectiveSettings || {});
    } catch (err) {
      console.error('Error fetching CBC settings:', err);
      setError(err.response?.data?.error || 'Failed to load CBC settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleOverride = async (enabled) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await api.put(`/service-groups/${groupId}/cbc/toggle-override`, { enabled });

      setSettings(prev => ({ ...prev, overrideEnabled: enabled }));
      setSuccess(`Service group override ${enabled ? 'enabled' : 'disabled'}`);
      fetchSettings(); // Refresh to get updated effective settings
    } catch (err) {
      console.error('Error toggling override:', err);
      setError(err.response?.data?.error || 'Failed to toggle override');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSettings = async (updates) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const res = await api.put(`/service-groups/${groupId}/cbc`, { overrideEnabled: true, ...updates });

      setSettings(res.data.cbcOverrides);
      setSuccess('Settings updated successfully');
      fetchSettings(); // Refresh effective settings
    } catch (err) {
      console.error('Error updating settings:', err);
      setError(err.response?.data?.error || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = async () => {
    if (!window.confirm('Reset service group CBC settings to platform defaults?')) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await api.delete(`/service-groups/${groupId}/cbc/override`);

      setSuccess('Reset to platform defaults');
      fetchSettings();
    } catch (err) {
      console.error('Error resetting settings:', err);
      setError(err.response?.data?.error || 'Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  if (!groupId) {
    return <div style={styles.container}>Select a service group to configure CBC settings.</div>;
  }

  if (loading) {
    return <div style={styles.container}>Loading CBC settings...</div>;
  }

  const isOverrideEnabled = settings?.overrideEnabled;

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>CBC Settings for {groupName || 'Service Group'}</h3>
      <p style={styles.subtitle}>
        Configure Conversational Boundary Control overrides for this service group.
        When enabled, these settings will override platform defaults for all clients in this group.
      </p>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {/* Current Effective Settings */}
      <div style={styles.effectiveBox}>
        <h4 style={styles.effectiveTitle}>Currently Active Settings</h4>
        <div style={styles.effectiveSource}>
          Source: <strong>{effectiveSettings?.source === 'service_group_override' ? 'Service Group Override' : 'Platform Default'}</strong>
        </div>
        {effectiveSettings?.enabled === false ? (
          <div style={styles.disabled}>CBC is disabled for this group</div>
        ) : (
          <div style={styles.effectiveDetails}>
            <div>Threshold Preset: <strong>{effectiveSettings?.thresholdPreset || 'moderate'}</strong></div>
            <div>Transparency Mode: <strong>{effectiveSettings?.transparencyMode || 'implicit_then_explicit'}</strong></div>
            {effectiveSettings?.resolvedThresholds && (
              <>
                <div>Soft Reconnect: {effectiveSettings.resolvedThresholds.softReconnectionThreshold} exchanges</div>
                <div>Explicit Reconnect: {effectiveSettings.resolvedThresholds.explicitReconnectionThreshold} exchanges</div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Override Toggle */}
      <div style={styles.section}>
        <h4 style={styles.sectionTitle}>Service Group Override</h4>
        <label style={styles.toggleLabel}>
          <input
            type="checkbox"
            checked={isOverrideEnabled}
            onChange={(e) => handleToggleOverride(e.target.checked)}
            disabled={saving}
            style={styles.checkbox}
          />
          <span style={styles.toggleText}>
            {isOverrideEnabled ? 'Override Active' : 'Using Platform Defaults'}
          </span>
        </label>
        <p style={styles.description}>
          When enabled, you can set custom CBC thresholds for this service group.
        </p>
      </div>

      {/* Override Settings (only shown when override is enabled) */}
      {isOverrideEnabled && (
        <>
          {/* Threshold Preset */}
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>Threshold Preset</h4>
            <select
              value={settings?.thresholdPreset || ''}
              onChange={(e) => handleUpdateSettings({ thresholdPreset: e.target.value })}
              disabled={saving}
              style={styles.select}
            >
              <option value="">Use Platform Default</option>
              <option value="conservative">Conservative (stricter boundaries)</option>
              <option value="moderate">Moderate (balanced)</option>
              <option value="permissive">Permissive (more flexible)</option>
            </select>
          </div>

          {/* Transparency Mode */}
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>Transparency Mode</h4>
            <select
              value={settings?.transparencyMode || ''}
              onChange={(e) => handleUpdateSettings({ transparencyMode: e.target.value })}
              disabled={saving}
              style={styles.select}
            >
              <option value="">Use Platform Default</option>
              <option value="implicit_only">Implicit Only</option>
              <option value="implicit_then_explicit">Implicit then Explicit</option>
              <option value="always_explicit">Always Explicit</option>
            </select>
          </div>

          {/* CBC Disable Option */}
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>Disable CBC for Group</h4>
            <label style={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={settings?.cbcDisabled === true}
                onChange={(e) => handleUpdateSettings({
                  cbcDisabled: e.target.checked,
                  disabledReason: e.target.checked ? 'Disabled by Clinical Supervisor' : undefined
                })}
                disabled={saving}
                style={styles.checkbox}
              />
              <span style={{ color: settings?.cbcDisabled ? '#dc2626' : 'inherit' }}>
                Completely disable CBC for this service group
              </span>
            </label>
            {settings?.cbcDisabled && (
              <input
                type="text"
                placeholder="Reason for disabling (optional)"
                value={settings?.disabledReason || ''}
                onChange={(e) => handleUpdateSettings({ disabledReason: e.target.value })}
                disabled={saving}
                style={{ ...styles.input, marginTop: '10px' }}
              />
            )}
          </div>

          {/* Reset Button */}
          <div style={styles.section}>
            <button
              onClick={handleResetToDefaults}
              disabled={saving}
              style={styles.resetButton}
            >
              Reset to Platform Defaults
            </button>
          </div>
        </>
      )}

      {/* Platform Presets Reference */}
      <div style={styles.section}>
        <h4 style={styles.sectionTitle}>Platform Presets Reference</h4>
        <div style={styles.presetGrid}>
          {Object.entries(platformPresets).map(([name, preset]) => (
            <div key={name} style={styles.presetCard}>
              <strong>{name.charAt(0).toUpperCase() + name.slice(1)}</strong>
              <div style={styles.presetDetails}>
                <div>Observe: {preset.observationThreshold}</div>
                <div>Soft: {preset.softReconnectionThreshold}</div>
                <div>Explicit: {preset.explicitReconnectionThreshold}</div>
                <div>Alert: {preset.alertThreshold}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '20px'
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '5px',
    color: '#333'
  },
  subtitle: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '20px'
  },
  error: {
    padding: '10px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    borderRadius: '6px',
    marginBottom: '15px',
    fontSize: '14px'
  },
  success: {
    padding: '10px',
    backgroundColor: '#d1fae5',
    color: '#059669',
    borderRadius: '6px',
    marginBottom: '15px',
    fontSize: '14px'
  },
  effectiveBox: {
    padding: '15px',
    backgroundColor: '#f0f9ff',
    border: '1px solid #bae6fd',
    borderRadius: '8px',
    marginBottom: '20px'
  },
  effectiveTitle: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '8px',
    color: '#0369a1'
  },
  effectiveSource: {
    fontSize: '13px',
    marginBottom: '8px'
  },
  effectiveDetails: {
    fontSize: '13px',
    color: '#555',
    lineHeight: '1.6'
  },
  disabled: {
    color: '#dc2626',
    fontWeight: '500'
  },
  section: {
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    border: '1px solid #e0e0e0'
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: '600',
    marginBottom: '10px',
    color: '#444'
  },
  description: {
    fontSize: '13px',
    color: '#666',
    marginTop: '8px'
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer'
  },
  toggleText: {
    fontSize: '14px',
    fontWeight: '500',
    marginLeft: '8px'
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer'
  },
  select: {
    width: '100%',
    padding: '8px',
    fontSize: '14px',
    borderRadius: '6px',
    border: '1px solid #ccc',
    backgroundColor: '#fff'
  },
  input: {
    width: '100%',
    padding: '8px',
    fontSize: '14px',
    borderRadius: '6px',
    border: '1px solid #ccc'
  },
  resetButton: {
    padding: '8px 16px',
    backgroundColor: '#f3f4f6',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  presetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '10px',
    marginTop: '10px'
  },
  presetCard: {
    padding: '10px',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    fontSize: '12px'
  },
  presetDetails: {
    marginTop: '5px',
    color: '#666',
    lineHeight: '1.5'
  }
};

export default ServiceGroupCBCSettings;
